import type { ThemeBreakpointIndexEnum } from '@inter-ikea-kompis/enums';
import type { ITheme, IThemeBreakpointSpecification } from '@inter-ikea-kompis/types';

type ICallbackOptions = {
	breakpointIndex: ThemeBreakpointIndexEnum;
	breakpoint: IThemeBreakpointSpecification | null;
};

/**
 * Theme breakpoint observer.
 */
export default class ThemeBreakpointObserver {
	private breakpoints: IThemeBreakpointSpecification[] | null = null;
	private currentBreakpointIndex: ThemeBreakpointIndexEnum | null = null;
	private timeout: number | NodeJS.Timeout | null = null;
	private listeners = {
		resize: this.onResize.bind(this)
	};
	private callback: ((options: ICallbackOptions) => void) | null = null;
	private element: HTMLElement | null = null;
	private resizeObserver: ResizeObserver | null = null;

	/**
	 * Constructor.
	 *
	 * @param callback Function.
	 * @param [element] Element.
	 */
	constructor(callback: (options: ICallbackOptions) => void, element: HTMLElement | null = null) {
		this.callback = callback;
		this.element = element;
	}

	/**
	 * Starts listening for resize events and emits events when the viewport size changes.
	 *
	 * @param theme Theme.
	 */
	public observe(theme: ITheme): void {
		if (this.currentBreakpointIndex === null) {
			if (this.element) {
				this.resizeObserver = new ResizeObserver(this.listeners.resize);
				this.resizeObserver.observe(this.element);
			} else {
				window.addEventListener('resize', this.listeners.resize);
			}
		}
		this.breakpoints = Object.values(theme.breakpoint).sort((a, b) => {
			return a.width - b.width;
		});

		// As the element usually isn't rendered here it will not have any CSS that gives it a size.
		// By setting display to "block" it will become 100% in width of its parent, which most likely will be the size it will become once it is rendered.
		// If it is not the same size when rendered, the ResizeObserver will trigger the callback for re-rendering and it will correct itself.
		// However, when a re-rendering occurr we will unfortunately get a re-paint, but hopefully it is unlikely.
		if (this.element && !this.element.style.display) {
			this.element.style.display = 'block';
		}

		const width = this.element ? this.element.offsetWidth : window.innerWidth;

		this.currentBreakpointIndex = this.findBreakpointIndex(width);

		if (this.element && this.element.style.display) {
			this.element.style.removeProperty('display');
		}
	}

	/**
	 * Disconnects the observer.
	 */
	public disconnect(): void {
		this.currentBreakpointIndex = null;

		clearTimeout(<number>this.timeout);

		this.currentBreakpointIndex = null;

		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		} else {
			window.removeEventListener('resize', this.listeners.resize);
		}
	}

	/**
	 * Returns the current breakpoint breakpoint specification.
	 *
	 * @returns Breakpoint.
	 */
	public getBreakpoint(): IThemeBreakpointSpecification | null {
		if (this.currentBreakpointIndex === null) {
			return null;
		}
		return this.breakpoints?.[this.currentBreakpointIndex] ?? null;
	}

	/**
	 * Returns the current breakpoint index.
	 *
	 * @returns Breakpoint index.
	 */
	public getBreakpointIndex(): ThemeBreakpointIndexEnum | null {
		return this.currentBreakpointIndex;
	}

	/**
	 * Triggered on resize.
	 */
	private onResize(): void {
		clearTimeout(<number>this.timeout);
		this.timeout = setTimeout(() => {
			const width = this.element ? this.element.offsetWidth : window.innerWidth;
			const breakpointIndex = this.findBreakpointIndex(width);
			if (breakpointIndex !== this.currentBreakpointIndex) {
				this.currentBreakpointIndex = breakpointIndex;
				this.callback?.({ breakpointIndex, breakpoint: this.getBreakpoint() });
			}
		});
	}

	/**
	 * Finds and returns the breakpoint index matching a width.
	 *
	 * @param width Width.
	 * @returns Breakpoint index.
	 */
	private findBreakpointIndex(width: number): ThemeBreakpointIndexEnum {
		if (!this.breakpoints || this.breakpoints.length === 0) {
			return 0;
		}

		const xxlarge = this.breakpoints[this.breakpoints.length - 1];

		if (width >= xxlarge.width) {
			return this.breakpoints.length - 1;
		}

		for (let i = this.breakpoints.length - 1; i >= 0; i--) {
			if (this.breakpoints[i].width <= width) {
				return i;
			}
		}

		return 0;
	}
}
